import React from 'react';
import { createRoot } from 'react-dom/client';
import smoothscroll from 'smoothscroll-polyfill';
import * as Sentry from '@sentry/react';
import escapeRegExp from 'lodash/escapeRegExp';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import { User } from '@front/common/src/types/commons';
import { initialize } from '@front/common/src/utils/initialize';
import { errorNotifier, Level, registerErrorNotifier } from '@front/common/src/errors/error-notifier';

import { web } from '@front/i18n';

import { getWebAppLocale, setCountry } from '@front/web-ui/src/utils/country';

import initializeStore from './store';
import Config from './utils/config';
import App from './app';
import { initializeTracking } from './utils/tracking';

initializeTracking(import.meta.env.VITE_REACT_APP_AMPLITUDE_API_KEY || '');

smoothscroll.polyfill();

const tracePropagationTargetsSources = [
  'https://www.wecasa.fr/api',
  'https://aws-stage.wecasa.fr/api',
  'https://www.wecasa.co.uk/api',
  'https://aws-stage.wecasa.co.uk/api',
  'https://www.wecasa.de/api',
  'https://aws-stage.wecasa.de/api',
  'https://www.wecasa.at/api',
  'https://aws-stage.wecasa.at/api',
  'https://www.wecasa.ch/api',
  'https://aws-stage.wecasa.ch/api',
].map((url) => new RegExp(`^${escapeRegExp(url)}`));

Sentry.init({
  dsn: Config.SENTRY_API_KEY,
  environment: import.meta.env.MODE === 'production' ? 'production' : 'staging',
  sendDefaultPii: true,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', ...tracePropagationTargetsSources],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%.
  replaysOnErrorSampleRate: 1.0, // Change the sample rate to 100% when sampling sessions where errors occur.
});

registerErrorNotifier({
  notify: (error: Error, level?: Level) => Sentry.captureException(error, { level }),
  setUser: (user: User | null) =>
    user
      ? Sentry.setUser({ id: user.public_id, email: user.email, username: `${user.first_name} ${user.last_name}` })
      : Sentry.setUser(null),
});

/*
 * Here we need to wait for the end of the initialization before we render the components
 * to be sure the translations are correctly initialized before we use them.
 * As the initialization is not inside an async context we cannot use await.
 * This is why we do a then here
 */
initialize({
  appName: Config.APP_NAME,
  authToken: `Bearer ${window.wecasa.token}`,
  baseURL: `${window.wecasa.siteUrl}api/v1`,
  errorNotifier,
  i18nConfig: { translations: web, locale: getWebAppLocale() },
  setCountry,
}).then(() => {
  const { store, persistor } = initializeStore();
  const root = createRoot(document.getElementById('root')!);
  root.render(<App store={store} persistor={persistor} />);
});
